import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { registerPushToken, onForegroundMessage } from '../lib/firebase';
import AccountDeactivatedPopup from '../components/common/AccountDeactivatedPopup';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [isLoading, setIsLoading] = useState(true);
  const [deactivationInfo, setDeactivationInfo] = useState(null);

  // Configure axios defaults
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Fetch current user
  const fetchUser = useCallback(async () => {
    if (!token) {
      setIsLoading(false);
      return;
    }

    try {
      // Use /auth/profile to get full user data including permissions for managers
      const response = await axios.get(`${API}/auth/profile`);
      const userData = response.data;
      
      // Check if user is deactivated
      if (userData && !userData.is_active) {
        setDeactivationInfo({
          reason: userData.deactivation_reason || 'Your account has been deactivated',
          deactivatedAt: userData.deactivated_at,
          email: userData.email,
          name: userData.name,
          userId: userData.id
        });
      } else {
        setDeactivationInfo(null);
      }
      
      setUser(userData);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      
      // Token is invalid, clear it
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);
      setDeactivationInfo(null);
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  // Poll for account status changes (e.g., admin deactivation/reactivation while user is logged in)
  // This allows the suspension popup to appear/disappear immediately without requiring logout/login
  useEffect(() => {
    if (!token) return;
    
    // Check status every 10 seconds
    const statusCheckInterval = setInterval(async () => {
      try {
        const response = await axios.get(`${API}/auth/profile`);
        const userData = response.data;
        
        if (!userData) return;
        
        const wasDeactivated = !!deactivationInfo;
        const isNowDeactivated = !userData.is_active;
        
        // If user was active but is now deactivated, show the popup immediately
        if (isNowDeactivated && !wasDeactivated) {
          console.log('Account deactivated - showing suspension popup');
          setDeactivationInfo({
            reason: userData.deactivation_reason || 'Your account has been deactivated',
            deactivatedAt: userData.deactivated_at,
            email: userData.email,
            name: userData.name,
            userId: userData.id
          });
          setUser(userData);
        }
        // If user was deactivated but is now reactivated, clear the popup and reload
        else if (!isNowDeactivated && wasDeactivated) {
          console.log('Account reactivated - clearing suspension popup');
          setDeactivationInfo(null);
          setUser(userData);
          // Force page reload to ensure clean state
          window.location.reload();
        }
      } catch (error) {
        // Silently fail - don't log out user on polling errors
        console.debug('Status check failed:', error.message);
      }
    }, 10000); // Check every 10 seconds
    
    return () => clearInterval(statusCheckInterval);
  }, [token, deactivationInfo]);

  // Set up foreground push notification handler with in-app toast
  useEffect(() => {
    if (!user || !token) return;
    
    // Import toast dynamically to avoid circular dependencies
    const handleForegroundNotification = async (payload) => {
      console.log('Foreground notification:', payload);
      
      // Show in-app toast notification
      const { toast } = await import('sonner');
      const { title, body } = payload.notification || {};
      
      toast.info(title || 'New Notification', {
        description: body || 'You have a new notification',
        duration: 5000,
        action: {
          label: 'View',
          onClick: () => {
            // Navigate based on notification data
            const data = payload.data || {};
            if (data.booking_id) {
              window.location.href = `/booking/${data.booking_id}`;
            } else if (data.url) {
              window.location.href = data.url;
            }
          }
        }
      });
    };
    
    const unsubscribe = onForegroundMessage(handleForegroundNotification);
    return () => { if (typeof unsubscribe === 'function') unsubscribe(); };
  }, [user, token]);

  const login = async (credentials) => {
    try {
      const response = await axios.post(`${API}/auth/login`, credentials);
      const { access_token, user: userData, deactivated, deactivation_reason, deactivated_at } = response.data;
      
      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(userData);
      
      // Check if user is deactivated - allow login but set deactivation info
      if (deactivated || !userData.is_active) {
        setDeactivationInfo({
          reason: deactivation_reason || 'Your account has been deactivated',
          deactivatedAt: deactivated_at,
          email: userData.email,
          name: userData.name,
          userId: userData.id
        });
      } else {
        setDeactivationInfo(null);
      }
      
      // Register push token in background (non-blocking)
      registerPushToken(access_token).catch(() => {});
      
      return userData;
    } catch (error) {
      // Handle banned users (hard block)
      if (error.response?.status === 403) {
        throw new Error(error.response.data.detail || 'Access denied');
      }
      throw error;
    }
  };

  const register = async (userData) => {
    const response = await axios.post(`${API}/auth/register`, userData);
    const { access_token, user: newUser } = response.data;
    
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(newUser);
    
    return newUser;
  };

  const requestOTP = async (data) => {
    const response = await axios.post(`${API}/auth/otp/request`, data);
    return response.data;
  };

  const verifyOTP = async (data) => {
    const response = await axios.post(`${API}/auth/otp/verify`, data);
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setDeactivationInfo(null);
  };

  const updateUser = (updates) => {
    setUser(prev => ({ ...prev, ...updates }));
  };

  const refreshUser = fetchUser;

  const value = {
    user,
    token,
    isLoading,
    login,
    register,
    requestOTP,
    verifyOTP,
    logout,
    updateUser,
    fetchUser,
    refreshUser,
    deactivationInfo,
  };

  return (
    <AuthContext.Provider value={value}>
      {deactivationInfo && (
        <AccountDeactivatedPopup
          reason={deactivationInfo.reason}
          deactivatedAt={deactivationInfo.deactivatedAt}
          userEmail={deactivationInfo.email}
          userName={deactivationInfo.name}
          userId={deactivationInfo.userId}
        />
      )}
      {children}
    </AuthContext.Provider>
  );
};
